import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, MapPin, Phone, Facebook, Instagram, Star, Sparkles } from 'lucide-react';
import { siteInfo, issaBadgeHorizontal, socialLinks, winterHours } from '../data/mock';
import SubscribeForm from './SubscribeForm';
import DonateButton from './DonateButton';
import Logo from './Logo';

export default function Footer() {
  const { pathname } = useLocation();
  return (
    <footer className="bg-splash-green text-splash-cream mt-16 md:mt-24">
      <DonateButton />
      {/* Newsletter strip */}
      {pathname !== '/programs/group-membership' && <div className="bg-gradient-to-br from-splash-cream to-splash-creamDark">
        <div
          data-testid="footer-subscribe"
          className="max-w-7xl mx-auto px-6 py-10 grid gap-6 md:grid-cols-2 items-center"
        >
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-splash-coral/15 text-splash-coralDark px-3 py-1 text-[11px] font-semibold uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> First Lesson Free
            </div>
            <h3 className="font-display font-bold text-2xl md:text-3xl text-splash-green leading-tight">
              Get discounts, updates &amp; a free lesson.
            </h3>
            <p className="mt-2 text-splash-green/75 text-sm max-w-md">
              Sign up for our email list to unlock a code good for one free group lesson at your next enrollment.
            </p>
          </div>
          <div>
            <SubscribeForm variant="compact" source="footer" testIdPrefix="footer-subscribe" />
          </div>
        </div>
      </div>}

      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-12 md:grid-cols-12">
        {/* Brand */}
        <div className="md:col-span-4">
          <Logo size="lg" tone="light" />
          <p className="mt-5 text-splash-cream/80 leading-relaxed text-sm max-w-xs">
            Empowering children with lifesaving skills in a supportive, small-group environment. We operate primarily at 3003 Woodland Ave, Des Moines, IA 50312.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram-link"
              className="p-2 rounded-full bg-splash-cream/10 hover:bg-splash-coral btn-transition"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              data-testid="footer-facebook-link"
              className="p-2 rounded-full bg-splash-cream/10 hover:bg-splash-coral btn-transition"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={socialLinks.googleReviews}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-google-reviews-link"
              className="inline-flex items-center gap-1.5 rounded-full bg-splash-cream/10 hover:bg-splash-coral px-3 py-2 text-xs font-semibold btn-transition"
            >
              <Star className="w-3.5 h-3.5 fill-current" /> Review us on Google
            </a>
          </div>
        </div>

        {/* Explore */}
        <div className="md:col-span-2">
          <h4 className="font-display text-lg mb-4 text-white">Explore</h4>
          <ul className="space-y-2 text-sm text-splash-cream/85">
            <li><Link className="hover:text-splash-coral" to="/about-us">About Us</Link></li>
            <li><Link className="hover:text-splash-coral" to="/programs">Programs</Link></li>
            <li><Link className="hover:text-splash-coral" to="/programs/first-splash">First Splash</Link></li>
            <li><Link className="hover:text-splash-coral" to="/programs/foundations">Little Splash Foundations™</Link></li>
            <li><Link className="hover:text-splash-coral" to="/blog">Blog</Link></li>
            <li><Link className="hover:text-splash-coral" to="/enroll">Enrollment</Link></li>
            <li><Link className="hover:text-splash-coral" to="/faq">FAQ</Link></li>
            <li><Link className="hover:text-splash-coral" to="/book">Book Online</Link></li>
            <li><Link className="hover:text-splash-coral" to="/sponsor-a-lesson">Sponsor a Lesson</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-3">
          <h4 className="font-display text-lg mb-4 text-white">Contact</h4>
          <ul className="space-y-3 text-sm text-splash-cream/85">
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-splash-coral" />{siteInfo.address}</li>
            <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-splash-coral shrink-0" /><a className="hover:text-splash-coral break-all" href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a></li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-splash-coral shrink-0" /><a className="hover:text-splash-coral" href={`tel:${siteInfo.phone}`}>{siteInfo.phone}</a></li>
          </ul>
        </div>

        {/* Hours */}
        <div className="md:col-span-3">
          <h4 className="font-display text-lg mb-4 text-white">Winter 2026&ndash;2027 Hours</h4>
          <ul className="space-y-1.5 text-sm text-splash-cream/85" data-testid="footer-hours">
            {winterHours.map(([d, h]) => (
              <li key={d} className="flex gap-3">
                <span className="w-9 shrink-0 font-semibold text-white">{d}</span>
                <span className={h === 'Closed' ? 'text-splash-cream/50' : ''}>{h}</span>
              </li>
            ))}
          </ul>
          <Link to="/enroll" className="inline-flex mt-5 rounded-full bg-splash-coral hover:bg-splash-coralDark px-5 py-2.5 text-sm font-semibold text-white btn-transition">
            Enroll Now
          </Link>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-splash-cream/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div className="bg-white rounded-xl px-4 py-2 flex items-center shrink-0">
            <img src={issaBadgeHorizontal} alt="ISSA Accredited Swim School" loading="lazy" decoding="async" className="h-7 md:h-8 object-contain" />
          </div>
          <div className="text-xs text-splash-cream/60 leading-relaxed">
            <span>&copy; {new Date().getFullYear()} Little Splash Swim Program. All rights reserved.</span>
            <span className="mx-2 hidden md:inline">|</span>
            <span className="block md:inline mt-1 md:mt-0">Water safety &bull; Confidence &bull; Connection</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
